import { Injectable, inject } from '@angular/core';
import {
  ESISInformation,
  EsisPreviewFormModel,
  LendingDocumentInformationFormType,
  LendingDocumentInformationType,
  Product,
} from '@shared/models';
import { RulesEngineSchema, RulesEngineSchemaResponseModel } from '@shared/models/rules-engine';
import { ClubHubDataService } from '@shared/services';
import { LenderHubDataService } from '@shared/services/lenderhub-data.service';
import { Observable, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ESISService {
  private readonly lenderHubDataService = inject(LenderHubDataService);
  private readonly clubHubDataService = inject(ClubHubDataService);

  public getLendingFormTypes(): Observable<LendingDocumentInformationFormType[]> {
    return this.lenderHubDataService.get<LendingDocumentInformationFormType[]>('lenderEsisInformation/types');
  }

  public getESISInformation(
    lendingTypeId: number,
    documentType: LendingDocumentInformationType,
    getDraft = false,
  ): Observable<ESISInformation> {
    return this.lenderHubDataService.get<ESISInformation>(
      `lenderEsisInformation/${lendingTypeId}/${documentType}${getDraft ? '/Draft' : ''}`,
    );
  }

  public saveESISInformation(
    lendingTypeId: number,
    documentType: LendingDocumentInformationType,
    esisInformation: unknown,
    setLive = false,
  ) {
    return this.lenderHubDataService.put<ESISInformation>(
      `lenderEsisInformation/${lendingTypeId}/${documentType}?setLive=${setLive}`,
      esisInformation,
    );
  }

  public getPreviewProducts(lendingTypeId: number, documentType: LendingDocumentInformationType) {
    return this.clubHubDataService.get<Product[]>(`EsisPreview/Products?lendingTypeId=${lendingTypeId}&documentType=${documentType}`);
  }

  public previewESIS(
    documentType: LendingDocumentInformationType,
    lendingTypeId: number,
    esisInformation: ESISInformation,
    formData: EsisPreviewFormModel,
    productAttributes: Record<string, boolean> = {},
  ) {
    return this.clubHubDataService.postFileArrayBuffer('EsisPreview', {
      documentType,
      lendingTypeId,
      ...formData,
      templateData: esisInformation,
      productAttributes,
    });
  }

  public deleteDraftESIS(documentType: LendingDocumentInformationType, lendingTypeId: number) {
    return this.lenderHubDataService.delete<never>(`LenderEsisInformation/${lendingTypeId}/${documentType}/Draft`);
  }

  public getDisplayRulesSchema(lendingTypeId: number) {
    return this.lenderHubDataService
      .get<RulesEngineSchemaResponseModel>(`LenderEsisInformation/DisplayRulesSchema/${lendingTypeId}`)
      .pipe(map(r => new RulesEngineSchema(r)));
  }
}
